import { nodeConfig } from '@scripts/mixins';

const NAMESPACE = 'share-tools';
const DEFAULT_SELECTOR = '[data-js-share-tools]';

/**
 * @typedef {Object} ShareToolsSettings
 * @property {boolean|null} [nativeShare=null] - null auto-detects navigator.share. Set false to force popover.
 */
const DEFAULT_SETTINGS = {
	nativeShare: null
};

/**
 * Sets share link URLs. Uses navigator.share when available,
 * otherwise uses to the popover module.
 */
class ShareTools {
	/**
	 * @param {HTMLElement} node
	 * @param {ShareToolsSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;

		this.elToggle = this.elNode.querySelector('[data-js-share-tools-toggle]');
		this.elTools = this.elNode.querySelectorAll('[data-js-share-tool]');
	}

	init() {
		this._setShareUrls();

		if (this._supportsNativeShare() && this.elToggle) {
			this.elToggle.removeAttribute('popovertarget');
			this._onToggleClick = this._onToggleClick.bind(this);
			this.elToggle.addEventListener('click', this._onToggleClick);
		}
	}

	_setShareUrls() {
		const url = encodeURIComponent(window.location.href);
		const title = encodeURIComponent(document.title);

		const urls = {
			facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
			x: `https://x.com/intent/tweet?text=${title}&url=${url}`,
			linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`
		};

		this.elTools.forEach((tool) => {
			const platform = tool.dataset.jsShareTool;

			if (urls[platform]) {
				tool.href = urls[platform];
			}
		});
	}

	/** @returns {boolean} */
	_supportsNativeShare() {
		if (this.config.nativeShare !== null) return this.config.nativeShare;

		return typeof navigator.share !== 'undefined';
	}

	_onToggleClick() {
		navigator.share({
			title: document.title,
			url: window.location.href
		}).catch(() => {});
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new ShareTools(node, config);

		module.init();

		return module;
	});
}
